<template>
  <section class="mt-12 border-t border-neutral-300" v-if="sharedUsages?.length">
    <template v-for="(usage, index) in sharedUsages" :key="index">
      <AccordionItem
        :title="getAccordionTitle(usage, index)"
        :description="getAccordionDescription(index)"
        :open="activeAccordionKey === getAccordionKey(index)"
        :controlledOpen="true"
        @toggle="onAccordionToggle(index, $event)">
        <ComplexitySliderInput
          :modelValue="getCorrectionForIndex(index)"
          :usageIndex="index"
          :role="role"
          @update:modelValue="onCorrectionUpdate(index, $event)" />
        <section class="manual-complexity-box">
          <p class="manual-complexity-overview">
            {{ $t('project.setup.sections.complexity.hint.overview') }}
          </p>
          <div class="manual-complexity-row">
            <dl class="manual-complexity-breakdown">
              <dt>{{ $t('project.setup.sections.complexity.breakdown.base') }}</dt>
              <dd>{{ formatMinutes(getBreakdown(index).base) }}</dd>
              <dt>{{ $t('project.setup.sections.complexity.breakdown.factor') }}</dt>
              <dd>
                {{ getBreakdown(index).factor.toFixed(2) }}
                <span v-if="getBreakdown(index).override != null" class="manual-complexity-muted">
                  {{
                    $t('project.setup.sections.complexity.breakdown.from_sliders', {
                      value: getBreakdown(index).sliderFactor.toFixed(2),
                    })
                  }}
                </span>
              </dd>
              <dt>{{ $t('project.setup.sections.complexity.breakdown.result') }}</dt>
              <dd class="font-medium text-neutral-700">{{ formatMinutes(getBreakdown(index).result) }}</dd>
            </dl>
            <div class="manual-complexity-input-group">
              <label :for="`complexity-override-${role}-${index}`" class="manual-complexity-unit">
                {{ $t('project.setup.sections.complexity.override') }}
              </label>
              <div class="relative">
                <ComplexityFactorInput
                  :id="`complexity-override-${role}-${index}`"
                  :modelValue="getBreakdown(index).override"
                  @update:modelValue="onOverrideChange(index, $event)"
                  :class="{
                    'core-form-error': validator.getErrors(`usageCorrections.${index}.complexityFactorOverride`),
                  }"
                  class="manual-complexity-input" />
                <ErrorMessage :name="`usageCorrections.${index}.complexityFactorOverride`" class="error-message" />
                <div class="manual-complexity-reset">
                  <button
                    v-if="getBreakdown(index).override != null"
                    class="flex h-6 w-6 items-center justify-center text-neutral-500 hover:text-neutral-700"
                    @click.prevent="onOverrideChange(index, null)"
                    :title="$t('project.general.sections.usage_definition.usage.tip.reset')">
                    <IconX class="h-4 w-4" stroke="1.8" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AccordionItem>
    </template>
  </section>
  <section v-else class="my-24 core-alert-zone">
    {{ $t('project.setup.sections.complexity.hint.disabled') }}
  </section>
</template>

<script
  lang="ts"
  setup
  generic="T extends { buildingCost: number; constructionCost: number; usageCorrections: UsageCorrection[] }">
import { computed, ref, toRef, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'

import type { SharedUsageItem, UsageCorrection } from '@/schemas/general/types'

import { formatArea } from '@/helpers/format'
import { deepClone } from '@/helpers/objects_arrays'

import AccordionItem from '@/components/partials/layout/AccordionItem.vue'
import ComplexitySliderInput from '@/components/partials/inputs/ComplexitySliderInput.vue'
import ComplexityFactorInput from '@/components/partials/inputs/ComplexityFactorInput.vue'
import ErrorMessage from '@/components/shared/ErrorMessage.vue'
import { IconX } from '@tabler/icons-vue'

import { useValidator } from '@/validation/form-validator'
import { useWidgetText } from '@/helpers/useWidgetText'
import { getUsageDisplayTitle } from '@/helpers/usage-display'
import { useContentStore } from '@/stores/content'
import type { Role } from '@/stores/projectCenter/useCalculatorForRole'

export interface SetupCalculator {
  getMinutesPerSquareMeterForUsageAtIndex: (index: number) => number
  getMinutesPerSquareMeterForUsageAtIndexIgnoringManualComplexity: (index: number) => number
  getComplexityForUsageAtIndex: (index: number) => number
}

const props = defineProps<{
  role: Role
  setupCalculator: SetupCalculator
  sharedUsages: SharedUsageItem[]
  quantityArea: number
}>()
const { t } = useI18n()
const { wt } = useWidgetText('widget-texts-project-general', toRef(props, 'role'))
const contentStore = useContentStore()

const modelValue = defineModel({ type: Object as PropType<T> })

const validator = useValidator()
const activeAccordionKey = ref<string | null>(null)

// Default correction template
const defaultCorrection = (index: number): UsageCorrection => ({
  usageIndex: index,
  manualArea: undefined,
  manualComplexity: undefined,
  // Legacy coordinate fields (kept for backward compatibility)
  x1: null,
  y1: null,
  x2: null,
  y2: null,
  x3: null,
  y3: null,
  x4: null,
  y4: null,
  problem1: undefined,
  problem2: undefined,
  problem3: undefined,
  problem4: undefined,
  // New slider fields (default 1.0)
  sliderCreative: 1.0,
  sliderTechnical: 1.0,
  sliderSpatial: 1.0,
  sliderOrganizational: 1.0,
  // Justification fields
  justificationCreative: undefined,
  justificationTechnical: undefined,
  justificationSpatial: undefined,
  justificationOrganizational: undefined,
})

function getAccordionKey(index: number): string {
  return `${props.role}:${index}`
}

function onAccordionToggle(index: number, open: boolean): void {
  activeAccordionKey.value = open ? getAccordionKey(index) : null
}

function getCorrectionForIndex(index: number): UsageCorrection {
  const existing = modelValue.value?.usageCorrections?.find((c) => c.usageIndex === index)
  if (existing) return existing
  return defaultCorrection(index)
}

function onCorrectionUpdate(index: number, correction: UsageCorrection): void {
  if (!modelValue.value) return

  const corrections = deepClone(modelValue.value.usageCorrections || [])
  const existingIndex = corrections.findIndex((c) => c.usageIndex === index)

  if (existingIndex >= 0) {
    corrections[existingIndex] = correction
  } else {
    corrections.push(correction)
  }

  modelValue.value = {
    ...modelValue.value,
    usageCorrections: corrections,
  }
}

/** Sets or clears the factor override. The slider values are left untouched; a legacy min/m² override is replaced. */
function onOverrideChange(index: number, value: number | null): void {
  if (!modelValue.value) return
  if (getCorrectionForIndex(index).complexityFactorOverride === value) return

  const corrections = deepClone(modelValue.value.usageCorrections || [])
  const existingIndex = corrections.findIndex((c) => c.usageIndex === index)
  const correction = existingIndex >= 0 ? corrections[existingIndex] : defaultCorrection(index)
  correction.complexityFactorOverride = value
  correction.manualComplexity = null
  if (existingIndex < 0) corrections.push(correction)

  modelValue.value = {
    ...modelValue.value,
    usageCorrections: corrections,
  }
}

const getAccordionTitle = computed(
  () =>
    (usage: SharedUsageItem, index: number): string =>
      getUsageDisplayTitle(
        usage as SharedUsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null },
        wt,
        {
          allUsages: props.sharedUsages as Array<
            SharedUsageItem & { buildingTypeId?: string | null; buildingTypeName?: string | null }
          >,
          index,
          resolveBuildingTypeName: contentStore.getBuildingTypeNameById,
        }
      )
)

const getAccordionDescription = computed(() => (index: number): string => {
  const usage = props.sharedUsages[index]
  if (!usage) return ''

  const area = formatArea((props.quantityArea * usage.ratio) / 100)
  const areaText = t('project.setup.sections.complexity.share_area', { share: Math.round(usage.ratio), area })
  const result = Math.round(props.setupCalculator.getMinutesPerSquareMeterForUsageAtIndex(index))
  return areaText + '<span class="mx-2">|</span>' + result + ' ' + t('general.min_per_sq_m')
})

/** Base rate, applied factor and result for the breakdown box. A legacy min/m² override is shown as its equivalent factor. */
const getBreakdown = computed(() => (index: number) => {
  const correction = getCorrectionForIndex(index)
  const base = props.setupCalculator.getMinutesPerSquareMeterForUsageAtIndexIgnoringManualComplexity(index)
  const sliderFactor = props.setupCalculator.getComplexityForUsageAtIndex(index)
  const legacyFactor = correction.manualComplexity && base > 0 ? correction.manualComplexity / base : null
  const override = correction.complexityFactorOverride ?? legacyFactor
  return {
    base,
    sliderFactor,
    override,
    factor: override ?? sliderFactor,
    result: props.setupCalculator.getMinutesPerSquareMeterForUsageAtIndex(index),
  }
})

function formatMinutes(value: number): string {
  return Math.round(value) + ' ' + t('general.min_per_sq_m')
}
</script>

<style lang="postcss" scoped>
.manual-complexity-box {
  @apply mt-8 border border-neutral-300 bg-neutral-35 p-5;
}

.manual-complexity-row {
  @apply mt-3 grid grid-cols-1 gap-4 text-sm text-neutral-700 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end;
}

.manual-complexity-overview {
  @apply m-0 text-base font-medium text-neutral-700;
}

.manual-complexity-breakdown {
  @apply m-0 grid grid-cols-[auto_auto] justify-start gap-x-6 gap-y-1;
}

.manual-complexity-breakdown dt {
  @apply text-neutral-550;
}

.manual-complexity-breakdown dd {
  @apply m-0 tabular-nums;
}

.manual-complexity-muted {
  @apply ml-2 text-neutral-550;
}

.manual-complexity-input-group {
  @apply flex flex-col gap-1 lg:items-end;
}

.manual-complexity-input {
  @apply !mb-0 !w-24 !bg-white;
}

.manual-complexity-reset {
  @apply absolute right-1 top-2 flex w-6 items-center;
}

.manual-complexity-unit {
  @apply text-neutral-550;
}
</style>
